import { useCallback, useState } from 'react';
import type { DismissedNotice } from '@lds-efsp/shared-contracts';
import { useApi, API_BASE_URL } from './useApi';

type UserStatus = 'pending' | 'approved' | 'suspended' | 'all';

interface User {
  user_id: string;
  email: string;
  name?: string;
  picture?: string;
  created_at: string;
  last_login?: string;
  app_metadata?: Record<string, unknown>;
  user_metadata?: Record<string, unknown>;
}

interface UsersListResponse {
  users: User[];
  total: number;
  page: number;
  per_page: number;
}

interface UserActionResponse {
  success: boolean;
  message?: string;
  user?: User;
}

// GET /api/admin/users/:id/legal-acceptance (SOR-364) — the recorded terms and
// privacy acceptance of the ACCOUNT the user belongs to, plus that account's
// identity and its last dismissed Terms notice (SOR-457). Mirrors
// GET /api/legal/acceptance's vocabulary: nulls are explicit and mean "never
// accepted" / "never dismissed", never zero and never omitted. `dismissedNotice`
// is REQUIRED and always present, carrying the same `DismissedNotice` shape the
// caller-facing route answers under that key — the read reports it
// independently of whether an acceptance is recorded.
export interface UserLegalAcceptance {
  account: {
    id: string;
    name: string;
    accountNumber: string;
  } | null;
  accepted: {
    termsVersion: number | null;
    termsAcceptedAt: string | null;
    privacyVersion: number | null;
    privacyAcceptedAt: string | null;
  };
  dismissedNotice: DismissedNotice;
}

interface UpdateUserData {
  appMetadata?: Record<string, unknown>;
  userMetadata?: Record<string, unknown>;
}

interface UseAdminUsersApiReturn {
  getUsers: (status?: UserStatus, page?: number, perPage?: number) => Promise<UsersListResponse>;
  getPendingUsers: (page?: number, perPage?: number) => Promise<UsersListResponse>;
  getUser: (userId: string) => Promise<User>;
  getUserLegalAcceptance: (userId: string) => Promise<UserLegalAcceptance>;
  approveUser: (userId: string) => Promise<UserActionResponse>;
  rejectUser: (userId: string) => Promise<UserActionResponse>;
  suspendUser: (userId: string) => Promise<UserActionResponse>;
  reactivateUser: (userId: string) => Promise<UserActionResponse>;
  updateUser: (userId: string, data: UpdateUserData) => Promise<User>;
  sendPasswordReset: (userId: string) => Promise<UserActionResponse>;
  loading: boolean;
  error: string | null;
}

/**
 * Custom hook for admin user management API calls
 */
export function useAdminUsersApi(): UseAdminUsersApiReturn {
  const { get, post, patch } = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Get all users with optional status filter
   */
  const getUsers = useCallback(
    async (status: UserStatus = 'all', page = 0, perPage = 25): Promise<UsersListResponse> => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          status,
          page: String(page),
          per_page: String(perPage),
        });
        const result = await get<UsersListResponse>(`${API_BASE_URL}/api/admin/users?${params}`);
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [get]
  );

  /**
   * Get users pending approval
   */
  const getPendingUsers = useCallback(
    async (page = 0, perPage = 25): Promise<UsersListResponse> => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          page: String(page),
          per_page: String(perPage),
        });
        const result = await get<UsersListResponse>(`${API_BASE_URL}/api/admin/users/pending?${params}`);
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [get]
  );

  /**
   * Get a specific user by ID
   */
  const getUser = useCallback(
    async (userId: string): Promise<User> => {
      setLoading(true);
      setError(null);
      try {
        const result = await get<User>(`${API_BASE_URL}/api/admin/users/${encodeURIComponent(userId)}`);
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [get]
  );

  /**
   * Get the legal acceptance record of the account a user belongs to.
   * Deliberately side-effect-free — unlike the mutations above it does not
   * touch the hook's shared loading/error state, so a failed drill-in renders
   * its own error in the modal instead of raising the page-level
   * "Error loading users" banner. The caller owns the error surface.
   */
  const getUserLegalAcceptance = useCallback(
    async (userId: string): Promise<UserLegalAcceptance> => {
      return get<UserLegalAcceptance>(
        `${API_BASE_URL}/api/admin/users/${encodeURIComponent(userId)}/legal-acceptance`
      );
    },
    [get]
  );

  /**
   * Approve a pending user
   */
  const approveUser = useCallback(
    async (userId: string): Promise<UserActionResponse> => {
      setLoading(true);
      setError(null);
      try {
        const result = await post<UserActionResponse>(`${API_BASE_URL}/api/admin/users/${encodeURIComponent(userId)}/approve`);
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [post]
  );

  /**
   * Reject and delete a pending user
   */
  const rejectUser = useCallback(
    async (userId: string): Promise<UserActionResponse> => {
      setLoading(true);
      setError(null);
      try {
        const result = await post<UserActionResponse>(`${API_BASE_URL}/api/admin/users/${encodeURIComponent(userId)}/reject`);
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [post]
  );

  /**
   * Suspend a user (block login)
   */
  const suspendUser = useCallback(
    async (userId: string): Promise<UserActionResponse> => {
      setLoading(true);
      setError(null);
      try {
        const result = await post<UserActionResponse>(`${API_BASE_URL}/api/admin/users/${encodeURIComponent(userId)}/suspend`);
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [post]
  );

  /**
   * Reactivate a suspended user
   */
  const reactivateUser = useCallback(
    async (userId: string): Promise<UserActionResponse> => {
      setLoading(true);
      setError(null);
      try {
        const result = await post<UserActionResponse>(`${API_BASE_URL}/api/admin/users/${encodeURIComponent(userId)}/reactivate`);
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [post]
  );

  /**
   * Update user metadata
   */
  const updateUser = useCallback(
    async (userId: string, { appMetadata, userMetadata }: UpdateUserData): Promise<User> => {
      setLoading(true);
      setError(null);
      try {
        const result = await patch<User>(`${API_BASE_URL}/api/admin/users/${encodeURIComponent(userId)}`, {
          appMetadata,
          userMetadata,
        });
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [patch]
  );

  /**
   * Send password reset email
   */
  const sendPasswordReset = useCallback(
    async (userId: string): Promise<UserActionResponse> => {
      setLoading(true);
      setError(null);
      try {
        const result = await post<UserActionResponse>(`${API_BASE_URL}/api/admin/users/${encodeURIComponent(userId)}/password-reset`);
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [post]
  );

  return {
    // Methods
    getUsers,
    getPendingUsers,
    getUser,
    getUserLegalAcceptance,
    approveUser,
    rejectUser,
    suspendUser,
    reactivateUser,
    updateUser,
    sendPasswordReset,
    // State
    loading,
    error,
  };
}
